import { useEffect, useState } from 'react';
import {
  useAnalyzeTenderEstimateMutation,
  useDeleteTenderEstimateMutation,
  useGetTenderEstimateQuery,
  useLazyExportTenderEstimateXlsxQuery,
  useListTenderEstimatesQuery,
  useSetTenderPriceZoneMutation,
} from '@shared/api';
import { PriceZoneField, zoneLabel } from '../PriceZoneField';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { useAgentPermission } from '@entities/user';
import { useAppSelector } from '@app/hooks';
import { AnaloguesSection } from './AnaloguesSection';
import { TenderCalcFacts } from './TenderCalcFacts';
import { TenderTotalNote } from './TenderTotalNote';
import { IssuesTable } from './IssuesTable';
import { ItemsTable } from './ItemsTable';
import { PriceLevelSummary } from './PriceLevelSummary';
import {
  CheckRow,
  ErrMargin,
  Grid,
  Muted,
  ObjName,
  Row,
  Stack,
  TableTitle,
} from './styled';
import { TenderCreateForm } from './TenderCreateForm';
import {
  calcModeLabel,
  inputKindLabel,
  statusLabel,
  statusTone,
  TERMINAL,
} from './tenderLabels';
import { RegistryPager, useRegistryPage, useSelectedRecordParam } from '../registryPaging';

export function TenderEstimatesView() {
  const [selectedId, setSelectedId] = useSelectedRecordParam();
  const [poll, setPoll] = useState(0);
  const [exportError, setExportError] = useState<string | null>(null);
  const { can_write } = useAgentPermission('smeta');
  const currentUserId = useAppSelector((s) => s.auth.user?.id);

  const page = useRegistryPage();
  const listQ = useListTenderEstimatesQuery(
    { limit: page.limit, offset: page.offset },
    { pollingInterval: 5000 },
  );
  const [deleteEstimate] = useDeleteTenderEstimateMutation();
  const [analyzeEstimate, { isLoading: analyzing }] = useAnalyzeTenderEstimateMutation();
  const [setPriceZone] = useSetTenderPriceZoneMutation();
  const detailQ = useGetTenderEstimateQuery(selectedId ?? '', {
    skip: !selectedId,
    pollingInterval: poll,
  });
  const [exportXlsx, { isFetching: exporting }] = useLazyExportTenderEstimateXlsxQuery();

  useEffect(() => {
    if (!selectedId) {
      setPoll(0);
      return;
    }
    const st = detailQ.data?.estimate.status;
    setPoll(st && !TERMINAL.has(st) ? 2500 : 0);
  }, [selectedId, detailQ.data?.estimate.status]);

  async function handleDelete(id: string) {
    try {
      await deleteEstimate(id).unwrap();
      if (selectedId === id) setSelectedId(null);
    } catch {
      // ignore — список обновится сам при следующем поллинге
    }
  }

  async function handleExport(id: string) {
    setExportError(null);
    try {
      const blob = await exportXlsx(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tender_estimate_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError('Не удалось выгрузить XLSX расчёта.');
      // eslint-disable-next-line no-console
      console.error('exportTenderEstimateXlsx failed', err);
    }
  }

  const estimates = listQ.data?.items ?? [];
  const detail = detailQ.data;
  const isAuthor =
    !detail?.estimate.user_id ||
    !currentUserId ||
    detail.estimate.user_id === currentUserId;

  return (
    <Stack>
      <TenderCreateForm onCreated={(checkId) => setSelectedId(checkId)} />

      <Grid>
        <Stack>
          <RegistryPager
            label="Расчётов"
            page={page}
            shown={estimates.length}
            hasMore={listQ.data?.has_more}
            fetching={listQ.isFetching}
          />
          {estimates.map((c) => (
            <CheckRow key={c.id} $active={c.id === selectedId} onClick={() => setSelectedId(c.id)}>
              <Row>
                <ObjName>{c.object_name}</ObjName>
                <Badge $tone={statusTone(c.status)}>{statusLabel(c.status)}</Badge>
              </Row>
              <Row>
                <Muted>
                  {inputKindLabel(c.input_kind)} · {new Date(c.created_at).toLocaleString('ru-RU')}
                </Muted>
                {!TERMINAL.has(c.status) && c.progress_percent > 0 && (
                  <Muted>{c.progress_percent}%</Muted>
                )}
              </Row>
            </CheckRow>
          ))}
          {estimates.length === 0 && (
            <Muted>Пока нет расчётов. Загрузите перечень работ тендера выше.</Muted>
          )}
        </Stack>

        <div>
          {!selectedId && (
            <Muted>Выберите расчёт слева, чтобы увидеть сформированный ЛСР и ошибки.</Muted>
          )}
          {selectedId && !detail && <Muted>Загрузка…</Muted>}
          {detail && (
            <Card>
              <Row>
                <div>
                  <ObjName>{detail.estimate.object_name}</ObjName>
                  <Muted>
                    {inputKindLabel(detail.estimate.input_kind)} ·{' '}
                    {calcModeLabel(detail.estimate.calc_mode)} · позиций:{' '}
                    {detail.items.length} · ошибок: {detail.issues.length}
                    {detail.estimate.region ? ` · ${detail.estimate.region}` : ''}
                    {detail.estimate.price_zone
                      ? `, ${zoneLabel(detail.estimate.price_zone)}`
                      : ''}
                  </Muted>
                </div>
                <Row>
                  <Badge $tone={statusTone(detail.estimate.status)}>
                    {statusLabel(detail.estimate.status)}
                  </Badge>
                  {!TERMINAL.has(detail.estimate.status) ? null : (
                    <Button
                      $variant="ghost"
                      $size="sm"
                      type="button"
                      disabled={analyzing || !can_write}
                      title={!can_write ? 'Только чтение' : undefined}
                      onClick={() => analyzeEstimate(detail.estimate.id)}
                    >
                      {analyzing ? 'Запуск…' : 'Пересчитать'}
                    </Button>
                  )}
                  {detail.estimate.status === 'completed' && (
                    <Button
                      $variant="secondary"
                      $size="sm"
                      type="button"
                      onClick={() => handleExport(detail.estimate.id)}
                      disabled={exporting}
                    >
                      {exporting ? 'Готовлю…' : 'Скачать ЛСР (XLSX)'}
                    </Button>
                  )}
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={!can_write || !isAuthor}
                    title={
                      !can_write
                        ? 'Только чтение'
                        : !isAuthor
                          ? 'Удалить расчёт может только его автор'
                          : undefined
                    }
                    onClick={() => handleDelete(detail.estimate.id)}
                  >
                    Удалить
                  </Button>
                </Row>
              </Row>
              <TenderCalcFacts detail={detail} canWrite={can_write} />

              {detail.estimate.status === 'failed' && detail.estimate.error_message && (
                <ErrMargin>{detail.estimate.error_message}</ErrMargin>
              )}
              {exportError && <ErrMargin>{exportError}</ErrMargin>}
              <PriceZoneField
                key={detail.estimate.id}
                owner={detail.estimate.id}
                region={detail.estimate.region}
                zone={detail.estimate.price_zone}
                canWrite={can_write}
                onSave={(zone) =>
                  setPriceZone({ id: detail.estimate.id, price_zone: zone }).unwrap()
                }
              />
              {detail.estimate.status === 'completed' && (
                <PriceLevelSummary summary={detail.estimate.summary}>
                  <TenderTotalNote vat={detail.vat} />
                </PriceLevelSummary>
              )}

              <TableTitle>
                Ошибки и исправления <Badge $tone="neutral">{detail.issues.length}</Badge>
              </TableTitle>
              <IssuesTable items={detail.issues} estimateId={detail.estimate.id} />

              <TableTitle>
                Сформированная смета (ЛСР) <Badge $tone="neutral">{detail.items.length}</Badge>
              </TableTitle>
              {detail.estimate.status !== 'completed' && (
                <Muted>
                  Позиции показаны как разобраны из файла. Пока расчёт не завершён, стоимость
                  пустая — это состояние очереди, а не результат подбора расценок.
                </Muted>
              )}
              <ItemsTable
                items={detail.items}
                estimateId={detail.estimate.id}
                region={detail.estimate.region}
                objectType={detail.estimate.object_type}
              />

              <TableTitle>Сравнение с аналогами</TableTitle>
              <AnaloguesSection
                profile={detail.profile}
                cohort={detail.cohort}
                benchmarks={detail.benchmarks}
                ncs={detail.ncs}
              />
            </Card>
          )}
        </div>
      </Grid>
    </Stack>
  );
}
